import { useCallback, useEffect, useRef, useState } from "react";
import "@/App.css";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Manifesto from "@/components/Manifesto";
import Collection from "@/components/Collection";
import Atelier from "@/components/Atelier";
import Broderies from "@/components/Broderies";
import Contact from "@/components/Contact";
import Faq from "@/components/Faq";
import Signature from "@/components/Signature";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import AdminPage from "@/components/AdminPage";

function Site() {
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    lenisRef.current = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    if (cartOpen) lenisRef.current?.stop();
    else lenisRef.current?.start();
  }, [cartOpen]);

  const scrollTo = useCallback((hash) => {
    lenisRef.current?.scrollTo(hash, { offset: -72, duration: 1.4 });
  }, []);

  const addToCart = useCallback((product, variant, qty = 1) => {
    setCart((prev) => {
      const key = `${product.id}__${variant.label}`;
      const found = prev.find((i) => i.key === key);
      if (found)
        return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      return [...prev, { key, product, variant, qty }];
    });
    toast.success("Ajouté au panier", {
      description: `${product.name} — ${variant.label}`,
    });
  }, []);

  const updateQty = useCallback((key, delta) => {
    setCart((prev) =>
      prev.map((i) =>
        i.key === key ? { ...i, qty: Math.max(1, i.qty + delta) } : i
      )
    );
  }, []);

  const removeItem = useCallback((key) => {
    setCart((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get("paiement");
    const sessionId = params.get("session_id");
    if (!outcome) return;
    window.history.replaceState({}, "", window.location.pathname);
    if (outcome === "succes" && sessionId) {
      fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`
      )
        .then((r) => r.json())
        .then((data) => {
          if (data.payment_status === "paid") {
            clearCart();
            toast.success("Paiement confirmé, merci !", {
              description:
                "Votre commande est transmise à l'atelier. Confection : 15 jours à 1 mois.",
            });
          } else {
            toast.info("Paiement en cours de vérification", {
              description: "La confirmation intervient sous quelques instants.",
            });
          }
        })
        .catch(() => toast.info("Paiement en cours de vérification"));
    } else if (outcome === "annule") {
      toast.info("Paiement annulé", {
        description:
          "Votre panier est conservé, vous pouvez réessayer quand vous voulez.",
      });
    }
  }, [clearCart]);

  const count = cart.reduce((s, i) => s + i.qty, 0);

  return (
    <div className="App min-h-screen bg-[#0C0D0E] text-[#F7F5F0] font-sans antialiased">
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.035] mix-blend-overlay z-40"
        style={{
          backgroundImage: "radial-gradient(#fff 1px, transparent 1px)",
          backgroundSize: "16px 16px",
        }}
      />
      <Nav
        cartCount={count}
        onCartOpen={() => setCartOpen(true)}
        onNavigate={scrollTo}
      />
      <main>
        <Hero onNavigate={scrollTo} />
        <Broderies onNavigate={scrollTo} />
        <Marquee />
        <Manifesto />
        <Signature />
        <Collection onAdd={addToCart} />
        <Atelier />
        <Faq />
        <Contact />
      </main>
      <Footer onNavigate={scrollTo} />
      <WhatsAppFloat />
      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        cart={cart}
        onQty={updateQty}
        onRemove={removeItem}
        onClear={clearCart}
      />
      <Toaster theme="dark" position="bottom-right" richColors closeButton />
    </div>
  );
}

function App() {
  if (window.location.pathname === "/admin") {
    return (
      <>
        <AdminPage />
        <Toaster theme="dark" position="bottom-right" richColors closeButton />
      </>
    );
  }
  return <Site />;
}

export default App;
